import { useEffect, useState } from "react";
import { CalendarDays, CalendarPlus, Clock, MapPin, Share2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { downloadIcs } from "@/lib/calendar";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";
import EventCalendar, { pinDate } from "@/components/EventCalendar";
import { DIRECTIONS_URL } from "@/hooks/SettingsContext";
import { trackEventRegistration, trackDirectionsClick } from "@/lib/analytics";

function EventCard({ e }) {
  const share = async () => {
    const url = `${window.location.origin}/events`;
    const text = `${e.title} - ${e.date} at Victory Chapel, Clay County FL`;
    if (navigator.share) {
      try { await navigator.share({ title: e.title, text, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copied to clipboard");
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-navy/10 bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift" data-testid={`event-card-${e.id}`}>
      {e.image ? (
        <img src={e.image} alt={`${e.title} event image`} className="aspect-[16/9] w-full object-cover" loading="lazy" />
      ) : (
        <div className="flex aspect-[16/9] items-center justify-center bg-navy">
          <CalendarDays size={40} className="text-white/30" aria-hidden="true" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-7">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-2xl font-800 tracking-tight text-navy">{e.title}</h2>
          {e.sample && <span className="shrink-0 rounded-full bg-victory px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">Sample</span>}
        </div>
        <ul className="mt-4 space-y-2 text-sm text-slate-600">
          <li className="flex items-center gap-2.5"><CalendarDays size={15} className="text-victory" aria-hidden="true" /> {e.date}</li>
          {e.time && <li className="flex items-center gap-2.5"><Clock size={15} className="text-victory" aria-hidden="true" /> {e.time}</li>}
          {e.location && <li className="flex items-center gap-2.5"><MapPin size={15} className="text-victory" aria-hidden="true" /> {e.location}</li>}
        </ul>
        {e.description && <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">{e.description}</p>}
        <div className="mt-6 flex flex-wrap gap-2.5">
          {e.registration_url && (
            <a href={e.registration_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEventRegistration(e.title)}
               data-testid={`event-register-${e.id}`}
               className="inline-flex items-center gap-1.5 rounded-full bg-victory px-5 py-2.5 font-display text-xs font-700 text-white transition-colors hover:bg-victory-dark">
              REGISTER <ArrowRight size={13} aria-hidden="true" />
            </a>
          )}
          <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" onClick={trackDirectionsClick}
             data-testid={`event-directions-${e.id}`}
             className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2.5 font-display text-xs font-700 text-navy transition-colors hover:bg-navy hover:text-white">
            DIRECTIONS
          </a>
          <button type="button" onClick={share} data-testid={`event-share-${e.id}`} aria-label={`Share ${e.title}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2.5 font-display text-xs font-700 text-navy transition-colors hover:bg-navy hover:text-white">
            <Share2 size={13} aria-hidden="true" /> SHARE
          </button>
          {pinDate(e.date) && (
            <button type="button" onClick={() => { if (downloadIcs(e)) toast.success("Calendar file downloaded - open it to save the event"); }}
                    data-testid={`event-addcal-${e.id}`} aria-label={`Add ${e.title} to your calendar`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2.5 font-display text-xs font-700 text-navy transition-colors hover:bg-navy hover:text-white">
              <CalendarPlus size={13} aria-hidden="true" /> ADD TO CALENDAR
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Events() {
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://vcclaychurch.com/" },
      { "@type": "ListItem", position: 2, name: "Events" },
    ],
  };
  const eventJsonLd = data.upcoming.length ? {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbLd,
      {
        "@type": "ItemList",
        itemListElement: data.upcoming.map((e, i) => ({
          "@type": "ListItem", position: i + 1,
          item: {
            "@type": "Event", name: e.title, startDate: e.date,
            location: { "@type": "Place", name: e.location || "Victory Chapel", address: "3180 CR 220 STE 6-7, Middleburg, FL 32068" },
            description: e.description,
            organizer: { "@type": "Church", name: "Victory Chapel Clay" },
          },
        })),
      },
    ],
  } : breadcrumbLd;

  return (
    <>
      <SEO
        title="Events - What's Happening at Victory Chapel"
        description="Upcoming services, outreach and community events at Victory Chapel in Clay County, FL - including Service #1, launching January 2027."
        path="/events"
        jsonLd={eventJsonLd}
      />
      <section className="grain relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">Events</p>
          <LineReveal className="mt-4 font-display text-5xl font-900 tracking-tighter sm:text-7xl" lines={["LIFE TOGETHER,", "ON PURPOSE."]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Services, outreach and community moments at Victory Chapel. Come be part of it.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-24" data-testid="events-list-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {!loading && data.upcoming.length > 0 && (
            <Reveal>
              <div className="mb-14">
                <EventCalendar events={data.upcoming} />
                {data.upcoming.some((e) => !pinDate(e.date)) && (
                  <p className="mt-4 text-center text-xs text-slate-400" data-testid="undated-events-note">
                    Events without a set day (like Service #1 - January 2027) are listed below.
                  </p>
                )}
              </div>
            </Reveal>
          )}
          {loading ? (
            <p className="text-center text-slate-500">Loading events...</p>
          ) : data.upcoming.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-navy/20 bg-white p-12 text-center" data-testid="events-empty">
              <h2 className="font-display text-2xl font-800 text-navy">New events coming January 2027.</h2>
              <p className="mt-3 text-slate-600">It all starts with Service #1 - plan your visit and be there from the beginning.</p>
            </div>
          ) : (
            <Stagger className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {data.upcoming.map((e) => <StaggerItem key={e.id}><EventCard e={e} /></StaggerItem>)}
            </Stagger>
          )}

          {data.past.length > 0 && (
            <div className="mt-20">
              <Reveal>
                <h2 className="font-display text-2xl font-800 tracking-tight text-navy" data-testid="past-events-heading">Past Events</h2>
              </Reveal>
              <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {data.past.map((e) => (
                  <div key={e.id} className="rounded-2xl border border-navy/10 bg-white/60 p-6 opacity-70" data-testid={`past-event-${e.id}`}>
                    <h3 className="font-display text-lg font-700 text-navy">{e.title}</h3>
                    <p className="mt-1 text-sm text-slate-500">{e.date}{e.time ? ` - ${e.time}` : ""}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
